import {
  getMeasurementAnswer,
  type MeasurementMarketChallengeConfig,
} from './MeasurementMarketConfig.js';

export type MeasurementMarketPhase = 'estimate' | 'measure' | 'complete';
export type MeasurementMarketCheckResult = 'complete' | 'estimate-complete' | 'incorrect' | 'not-ready';

export interface MeasurementMarketSerializedState {
  readonly version: 1;
  readonly phase: MeasurementMarketPhase;
  readonly selectedValue?: number;
  readonly hintStage: number;
  readonly attempts: number;
}

export interface MeasurementMarketSnapshot extends MeasurementMarketSerializedState {
  readonly answer: number;
  readonly choices: readonly number[];
  readonly hint?: string;
}

export class MeasurementMarketState {
  private phase: MeasurementMarketPhase;
  private selectedValue?: number;
  private hintStage = 0;
  private attempts = 0;

  constructor(
    readonly config: MeasurementMarketChallengeConfig,
    saved?: Partial<MeasurementMarketSerializedState>,
  ) {
    this.phase = config.kind === 'measure' ? 'estimate' : 'measure';
    if (saved?.version === 1) this.restore(saved);
  }

  select(value: number) {
    if (this.phase === 'complete' || !this.snapshot.choices.includes(value)) return false;
    this.selectedValue = value;
    return true;
  }

  check(): MeasurementMarketCheckResult {
    if (this.phase === 'complete' || this.selectedValue === undefined) return 'not-ready';
    this.attempts += 1;
    if (this.phase === 'estimate' && this.config.kind === 'measure') {
      if (this.selectedValue !== this.config.estimateAnswer) return 'incorrect';
      this.phase = 'measure';
      this.selectedValue = undefined;
      this.hintStage = 0;
      return 'estimate-complete';
    }
    if (this.selectedValue !== getMeasurementAnswer(this.config)) return 'incorrect';
    this.phase = 'complete';
    return 'complete';
  }

  requestHint() {
    this.hintStage = Math.min(this.config.hints.length, this.hintStage + 1);
    return this.snapshot.hint;
  }

  serialize(): MeasurementMarketSerializedState {
    return {
      version: 1,
      phase: this.phase,
      selectedValue: this.selectedValue,
      hintStage: this.hintStage,
      attempts: this.attempts,
    };
  }

  get snapshot(): MeasurementMarketSnapshot {
    const choices = this.phase === 'estimate' && this.config.kind === 'measure'
      ? this.config.estimateChoices
      : this.config.kind === 'data'
        ? this.config.choices
        : Array.from({ length: 13 }, (_, value) => value);
    return {
      ...this.serialize(),
      answer: getMeasurementAnswer(this.config),
      choices,
      hint: this.hintStage > 0 ? this.config.hints[this.hintStage - 1] : undefined,
    };
  }

  private restore(saved: Partial<MeasurementMarketSerializedState>) {
    const allowedPhases: readonly MeasurementMarketPhase[] = this.config.kind === 'measure'
      ? ['estimate', 'measure', 'complete']
      : ['measure', 'complete'];
    if (saved.phase && allowedPhases.includes(saved.phase)) this.phase = saved.phase;
    const choices = this.snapshot.choices;
    if (Number.isInteger(saved.selectedValue) && choices.includes(Number(saved.selectedValue))) {
      this.selectedValue = Number(saved.selectedValue);
    }
    this.hintStage = Math.min(this.config.hints.length, Math.max(0, Number(saved.hintStage) || 0));
    this.attempts = Math.max(0, Number(saved.attempts) || 0);
  }
}
